import { useEffect } from 'react';
import { REALTIME_CHANNELS } from '../../utils/realtimeClient';

const useRealtimeSubscriptions = ({
  pusher,
  realtimeEnabled,
  realtimeConnected,
  queueProgramRefresh,
  setIsSyncing,
  setProgramSyncing,
  setSnackbar,
  formatAbortSummary,
  queryClient,
}) => {
  useEffect(() => {
    if (!realtimeEnabled || !pusher) return undefined;

    const handleProgramRefresh = (payload) => {
      if (!payload?.programName) return;
      queueProgramRefresh(payload.programName);
    };

    const handleRefreshStatus = (payload) => {
      setIsSyncing(payload?.status === 'in_progress');
      queryClient.invalidateQueries({ queryKey: ['cacheStatus'] });
    };

    const handleProgramStatus = (payload) => {
      const programs = payload?.programs || {};
      const activeProgram = Object.values(programs).some((status) => status === 'in_progress');
      setProgramSyncing(activeProgram);
      queryClient.invalidateQueries({ queryKey: ['cacheStatus'] });
    };

    const handleProgramError = (payload) => {
      if (!payload?.programName) return;
      setSnackbar({
        open: true,
        message: `${payload.programName} refresh issue: ${payload.message || 'Unknown error'}`,
        severity: 'warning',
      });
    };

    const handleRefreshAborted = (payload) => {
      setIsSyncing(false);
      setSnackbar({
        open: true,
        message: formatAbortSummary(payload),
        severity: 'warning',
      });
    };

    const refreshChannel = pusher.subscribe(REALTIME_CHANNELS.adminRefresh);
    refreshChannel.bind('refresh.status', handleRefreshStatus);
    refreshChannel.bind('refresh.program_status', handleProgramStatus);
    refreshChannel.bind('refresh.program_complete', handleProgramRefresh);
    refreshChannel.bind('refresh.program_error', handleProgramError);
    refreshChannel.bind('refresh.aborted', handleRefreshAborted);

    const logsChannel = pusher.subscribe(REALTIME_CHANNELS.adminDbLogs);
    const handleDbLogNew = () => {
      queryClient.invalidateQueries({ queryKey: ['dbLogs'] });
    };
    logsChannel.bind('dblog.new', handleDbLogNew);

    const cronChannel = pusher.subscribe(REALTIME_CHANNELS.adminCron);
    const handleCronJobsUpdated = (payload) => {
      queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
      const jobName = payload?.jobName;
      if (jobName) {
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey?.[0] === 'cronJobHistory' && query.queryKey?.[1] === jobName,
        });
      }
    };
    cronChannel.bind('cron.jobs.updated', handleCronJobsUpdated);
    cronChannel.bind('cron.job.run.started', handleCronJobsUpdated);
    cronChannel.bind('cron.job.run.completed', handleCronJobsUpdated);

    const metricsChannel = pusher.subscribe(REALTIME_CHANNELS.publicMetrics);
    const handleMetricsUpdated = () => {
      queryClient.invalidateQueries({ queryKey: ['applicationMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['statusChangeMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['dbSlowQueries'] });
    };
    metricsChannel.bind('metrics.updated', handleMetricsUpdated);

    const adminDbChannel = pusher.subscribe(REALTIME_CHANNELS.adminDb);
    const handleDbMigrationEvent = () => {
      queryClient.invalidateQueries({ queryKey: ['dbMigrationStatus'] });
    };
    adminDbChannel.bind('db.migration.tests.started', handleDbMigrationEvent);
    adminDbChannel.bind('db.migration.tests.completed', handleDbMigrationEvent);

    const vmtChannel = pusher.subscribe(REALTIME_CHANNELS.publicVmtSends);
    const handleVmtHistoryChanged = () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey?.[0] === 'vmtSendHistory',
      });
    };
    vmtChannel.bind('vmt.send.created', handleVmtHistoryChanged);
    vmtChannel.bind('vmt.send.updated', handleVmtHistoryChanged);
    vmtChannel.bind('vmt.send.deleted', handleVmtHistoryChanged);

    return () => {
      refreshChannel.unbind('refresh.status', handleRefreshStatus);
      refreshChannel.unbind('refresh.program_status', handleProgramStatus);
      refreshChannel.unbind('refresh.program_complete', handleProgramRefresh);
      refreshChannel.unbind('refresh.program_error', handleProgramError);
      refreshChannel.unbind('refresh.aborted', handleRefreshAborted);
      logsChannel.unbind('dblog.new', handleDbLogNew);
      cronChannel.unbind('cron.jobs.updated', handleCronJobsUpdated);
      cronChannel.unbind('cron.job.run.started', handleCronJobsUpdated);
      cronChannel.unbind('cron.job.run.completed', handleCronJobsUpdated);
      metricsChannel.unbind('metrics.updated', handleMetricsUpdated);
      adminDbChannel.unbind('db.migration.tests.started', handleDbMigrationEvent);
      adminDbChannel.unbind('db.migration.tests.completed', handleDbMigrationEvent);
      vmtChannel.unbind('vmt.send.created', handleVmtHistoryChanged);
      vmtChannel.unbind('vmt.send.updated', handleVmtHistoryChanged);
      vmtChannel.unbind('vmt.send.deleted', handleVmtHistoryChanged);
    };
  }, [
    formatAbortSummary,
    pusher,
    queryClient,
    queueProgramRefresh,
    realtimeEnabled,
    realtimeConnected,
    setIsSyncing,
    setProgramSyncing,
    setSnackbar,
  ]);
};

export default useRealtimeSubscriptions;
